import { useEffect, useRef, useState } from 'react'
import { Button, Dropdown, Option, MessageBar } from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { LICENSE_ICONS, DEFAULT_ICON_KEY } from '../lib/licenseIcons'
import ServiceIcon from './ServiceIcon'
import type { ServiceConfig, ServiceConfigResponse } from '../types'

// Must match api/src/lib/serviceIconBlob.ts's ALLOWED_ICON_CONTENT_TYPES/MAX_ICON_BYTES
const ALLOWED_ICON_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp', 'image/gif']
const MAX_ICON_BYTES = 512 * 1024

/** Icon shown before this service's name on the Dashboard and the Sidebar — either an
 * admin-uploaded logo image (takes priority once set) or a pick from the curated Fluent set
 * license types use. There's no reliably embeddable real-logo set covering every "service" this
 * app shows an icon for (Microsoft's own logo, notably, isn't in the common free brand-icon
 * packs due to trademark restrictions), so letting the admin upload their own beats guessing.
 * Standalone (not bundled with ServiceSettingsTab's other fields) so pages/PeopleForce.tsx —
 * which isn't a `MODULES` ServiceModule and has no license/sync-threshold concept — can still
 * offer the same icon picker without dragging in fields that don't apply to it. */
export default function ServiceIconPicker(props: { serviceId: string }) {
  const { serviceId } = props
  const [config, setConfig] = useState<ServiceConfig>({})
  const [loaded, setLoaded] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setLoaded(false)
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setConfig(res.config[serviceId] ?? {}))
      .finally(() => setLoaded(true))
  }, [serviceId])

  // Merge-patches only iconKey/customIconUrl — other fields on this service's config row (e.g.
  // ServiceSettingsTab's staleLoginDays) are set independently and untouched by this component,
  // same Table Storage Merge semantics every other per-field config editor in this app relies on.
  function save(patch: ServiceConfig) {
    setConfig(prev => ({ ...prev, ...patch }))
    apiFetchJson('/api/service-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serviceId, ...patch }),
    })
  }

  async function handleFileSelected(file: File) {
    setUploadError(null)
    if (!ALLOWED_ICON_TYPES.includes(file.type)) {
      setUploadError(`Unsupported file type "${file.type}" — use PNG, JPEG, SVG, WebP, or GIF.`)
      return
    }
    if (file.size > MAX_ICON_BYTES) {
      setUploadError(`Image too large — max ${MAX_ICON_BYTES / 1024} KB.`)
      return
    }
    setUploading(true)
    try {
      const res = await apiFetchJson<{ ok: boolean; url: string }>(
        `/api/service-icon-image?serviceId=${encodeURIComponent(serviceId)}`,
        { method: 'POST', headers: { 'Content-Type': file.type }, body: file },
      )
      setConfig(prev => ({ ...prev, customIconUrl: res.url }))
    } catch (e) {
      setUploadError(String(e))
    } finally {
      setUploading(false)
    }
  }

  const selectedIcon = config.iconKey ?? DEFAULT_ICON_KEY

  return (
    <div>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>Icon</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ServiceIcon config={config} size={28} />
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_ICON_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) handleFileSelected(file)
            e.target.value = ''
          }}
        />
        <Button size="small" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
          {uploading ? 'Uploading...' : 'Upload image...'}
        </Button>
        {config.customIconUrl && (
          <Button size="small" appearance="subtle" onClick={() => save({ customIconUrl: null })}>
            Remove
          </Button>
        )}
      </div>
      {uploadError && (
        <MessageBar intent="error" style={{ marginTop: 8 }}>
          {uploadError}
        </MessageBar>
      )}
      {!config.customIconUrl && (
        <Dropdown
          disabled={!loaded}
          value={selectedIcon}
          selectedOptions={[selectedIcon]}
          onOptionSelect={(_e, d) => {
            if (d.optionValue) save({ iconKey: d.optionValue })
          }}
          style={{ minWidth: 180, marginTop: 8 }}
        >
          {Object.keys(LICENSE_ICONS).map(key => (
            <Option key={key} value={key}>
              {key}
            </Option>
          ))}
        </Dropdown>
      )}
    </div>
  )
}
